import { useState, useEffect, useRef, lazy, Suspense } from 'react';
import { BIRTH_TIME_CAPTION } from '@/lib/birthTime';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { trackEvent, ymGoal } from '@/lib/analytics';
import { homeHandoffState } from '@/lib/homeHandoff';
import MainNavbar from '@/components/layout/MainNavbar';
import SEO from '@/components/SEO';
import useNatalCalculator from '@/components/natal/NatalCalculatorInline';
import NatalChartWheel from '@/components/natal/LazyChart';
import NatalWheelSkeleton from '@/components/NatalWheelSkeleton';
import { HousesTab, PlanetsTab, AspectsTab, WheelPreview, hasSavedChart, NatalFullStrip } from '@/components/natal/LazyChart';
import { useArticleLinks } from '@/lib/natalArticleLinks';
import { API_URL } from '@/lib/backendUrl';
import { SITE_URL, SITE_NAME_IS_DEFAULT } from '@/lib/siteConfig';
import NatalLimitModal from '@/components/natal/NatalLimitModal';
import { StickyFullReportCta } from '@/components/natal/NatalGuestFunnel';
import NatalPreviewBlocks from '@/components/natal/NatalPreviewBlocks';
import DayTodayCard from '@/components/natal/DayTodayCard';
import { ChevronDown, HelpCircle, Heart, HeartHandshake, Wallet, MousePointerClick, Lock, Sun, CalendarDays, CalendarRange } from 'lucide-react';
import { yearLabel } from '@/constants/periodPrograms';
import CategoriesBlock from '@/components/landing/CategoriesBlock';
import QuestionHero from '@/components/landing/QuestionHero';
import { PALETTE } from '../lib/themePalette';
import scrollUnderHeader, { headerOffset } from '@/lib/scrollUnderHeader';
import {
  ExpertBlock,
  PricingBlock,
  RegisterWhyBlock,
  WhyAstrofilBlock,
  AccordionSectionsBlock,
  SiteFooter,
} from '@/components/landing/HomeSections';
import CommentsBlock from '@/components/comments/CommentsBlock';
import { checkCalcUnlock, unlockIdFromUrl, loadCalcUnlock } from '@/lib/calcUnlock';

// Кнопка PDF и карточка «поделиться» показываются только тем, кто уже
// рассчитал свою карту, поэтому едут отдельными кусками, а не в main.js.
// Тяжёлые jspdf/html2canvas внутри PdfDownloadButton как были, так и остаются
// за динамическим импортом — этот слой их не касается.
const PdfDownloadButton = lazy(() => import('@/components/PdfDownloadButton'));
const NatalShareCard = lazy(() => import('@/components/natal/NatalShareCard'));
// Новые блоки натальной карты под полосой расшифровки (стихии, сила планет,
// основные показатели, таланты, карта в программах) — свой кусок, только после расчёта.
const NatalInsights = lazy(() => import('@/components/natal/insights/NatalInsights'));
// PDF-копия тех же плашек — тот же кусок, что и NatalInsights (один import).
const NatalInsightsPrint = lazy(() => import('@/components/natal/insights/NatalInsights')
  .then((m) => ({ default: m.NatalInsightsPrint })));
const PdfLinksPage = lazy(() => import('@/components/natal/insights/NatalInsights')
  .then((m) => ({ default: m.PdfLinksPage })));

// Красивые схематичные SVG-иллюстрации в фирменном стиле ASTROfeel (brand-фиолет + gold акценты)

// Фиолетовая полоса под шапкой убрана (решение владельца): она показывалась
// гостю, триальщику и бесплатному, причём гостю — вместе со строкой про
// ДВОЙНУЮ ВЫГОДУ, право на которую есть только у триала. На пути гостя
// рекламы больше нет: первое предложение ПРО человек видит в кабинете.
// ДВОЙНАЯ ВЫГОДА в кабинете не тронута.

// Заголовки разделов разбора. Кнопок-гармошек больше нет, но сам блок
// разбора под героем остался — он открывается голубыми строками полосы
// расшифровок, и ему нужен заголовок открытого раздела.
const PANEL_LABELS = {
  planets: 'Твои проявления',
  houses: 'Твои сферы жизни',
  aspects: 'Сценарии жизни',
};

// Заголовок «РАСШИФРОВКА ТВОЕЙ НАТАЛЬНОЙ КАРТЫ» и три кнопки-гармошки
// («Твои проявления», «Твои сферы жизни», «Сценарии жизни») из правой
// колонки убраны (решение владельца): расшифровка и так идёт текстом под
// колесом, а на их месте теперь карточки программ. Сами РАЗДЕЛЫ разбора
// никуда не делись — они открываются голубыми строками полосы расшифровок
// (openSectionFromStrip) и раскрываются тем же блоком под героем.

// Small deterministic string hash (DJB2-ish) — turns a long fingerprint
// string into a short id, just for a tidier data-pdf-cache-key attribute
// value. Not for security, just uniqueness for the same input.
function hashFingerprint(str) {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = (Math.imul(31, hash) + str.charCodeAt(i)) | 0;
  }
  return (hash >>> 0).toString(36);
}


// На ПК заголовок H1 стоит над кругом и формой, а колонка программ слева
// опущена ровно на его высоту (строка — leading-tight от того же clamp, плюс
// mb-2), поэтому верх первой плашки стоит на уровне верха карты. Строка
// целиком, без склейки: Tailwind находит классы в исходнике по полному тексту.
const HERO_TOP_OFFSET = 'lg:pt-[calc(clamp(15px,1.55vw,26px)_*_1.25_+_0.5rem)]';

/** Строка «Твоя карта готова» под плашкой дня — жирный текст, без рамки и фона. */
function ReadyPlate() {
  return (
    <p className="text-center text-sm font-bold text-slate-900">
      Твоя карта готова. Расшифровка ниже ↓
    </p>
  );
}

/**
 * Схематичный цветок-чакра: лотос из восьми лепестков вокруг круга. Рисуется
 * линией currentColor с той же толщиной и скруглениями, что иконки lucide на
 * соседних плашках, и принимает тот же className.
 */
function ChakraLotusIcon({ className = '' }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
    >
      {[0, 45, 90, 135, 180, 225, 270, 315].map((deg) => (
        <path
          key={deg}
          d="M12 8.9C9.9 7 9.9 3.8 12 1.3c2.1 2.5 2.1 5.7 0 7.6z"
          transform={`rotate(${deg} 12 12)`}
        />
      ))}
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

/** Карточка программы в правой колонке героя. */
function ProgramHeroCard({
  to = null, onClick = null, title, subtitle, Icon, gradient, program, badge = null,
  testId, source = 'landing_hero_cta', trackCalculatorType = null, tone = 'light',
  linkState = undefined,
}) {
  // tone="dark" — тёмно-синий текст для светлого фона («Твой день сегодня»):
  // белый на светло-голубом не читался. sky-950 на sky-300…400 — контраст
  // 6.4:1 и выше, мелкий подзаголовок читается с первого взгляда.
  const ink = tone === 'dark'
    ? { main: 'text-sky-950', sub: 'text-sky-950', iconBg: 'bg-white/45' }
    : { main: 'text-white', sub: 'text-white/85', iconBg: 'bg-white/15' };
  const className = `w-full flex items-center gap-3 rounded-xl p-2.5 text-left transition-all bg-gradient-to-r ${gradient} shadow-md hover:shadow-lg hover:-translate-y-0.5`;
  const body = (
    <>
      <span className={`flex-shrink-0 w-11 h-11 rounded-lg ${ink.iconBg} flex items-center justify-center`}>
        <Icon className={`w-6 h-6 ${ink.main}`} />
      </span>
      <span className="flex-1 min-w-0">
        <span className="flex items-center gap-1.5">
          <span className={`block text-sm font-bold leading-tight ${ink.main}`}>{title}</span>
          {badge && (
            <span className="px-1.5 py-0.5 rounded-full text-[9px] font-bold bg-amber-400 text-slate-900 flex-shrink-0">{badge}</span>
          )}
        </span>
        <span className={`block text-[11px] mt-0.5 leading-snug ${tone === 'dark' ? 'font-medium' : ''} ${ink.sub}`}>{subtitle}</span>
      </span>
      <ChevronDown className={`w-4 h-4 shrink-0 -rotate-90 ${ink.main}`} />
    </>
  );
  // Плашка без адреса — кнопка на этой же странице («Твой день сегодня»).
  if (!to) {
    return (
      <button type="button" onClick={onClick} data-testid={testId} className={className}>
        {body}
      </button>
    );
  }
  return (
    <Link
      to={to}
      state={linkState}
      style={{ scrollMarginTop: 'calc(4rem + var(--promo-bar-h, 0px))' }}
      onClick={() => {
        // Общая цель на все карточки колонки — видно, какая из программ
        // забирает переходы с главной.
        ymGoal('program_card_click', { program, source });
        // Воронка админки считает переходы в программы по click_calculate —
        // у хорара она была и остаётся.
        if (trackCalculatorType) {
          trackEvent('click_calculate', { calculator_type: trackCalculatorType, source });
        }
      }}
      data-testid={testId}
      className={className}
    >
      {body}
    </Link>
  );
}

/**
 * «Твой месяц» — сразу под «Когда придут деньги», «Твой день сегодня» —
 * под ним (порядок владелицы: Хорар, АстроЧакры, Любовь, Деньги, Твой
 * месяц, Твой день).
 */
function MonthHeroCard({ suffix = '', source = 'landing_hero_cta', linkState = undefined }) {
  return (
    <ProgramHeroCard
      to="/events-month"
      title="Твой месяц"
      subtitle="Твои события и возможности на 30 дней"
      Icon={CalendarDays}
      gradient="from-fuchsia-500 to-violet-600"
      program="events_month"
      testId={`hero-month-cta${suffix}`}
      source={source}
      linkState={linkState}
    />
  );
}

/**
 * «Твой 2027» (/year) — рядом с «Твоим месяцем»: общее колесо года.
 */
function YearHeroCard({ suffix = '', source = 'landing_hero_cta', linkState = undefined }) {
  return (
    <ProgramHeroCard
      to="/year"
      title={`Твой ${yearLabel()}`}
      subtitle="Удачные недели и главные события года"
      Icon={CalendarRange}
      gradient="from-indigo-500 to-sky-600"
      program="year"
      testId={`hero-year-cta${suffix}`}
      source={source}
      linkState={linkState}
    />
  );
}

/**
 * Карточки программ — левая колонка героя на ПК и первый блок страницы на
 * телефоне (одна копия на все ширины).
 */
function ProgramHeroCards({ suffix = '', source = 'landing_hero_cta', linkState = undefined }) {
  return (
    <>
      <ProgramHeroCard
        to="/horar"
        title="Хорар — вопрос сейчас"
        subtitle="Да/нет и срок за минуту"
        Icon={HelpCircle}
        gradient="from-accent-cta-500 to-accent-cta-600"
        program="horar"
        badge="ХИТ"
        trackCalculatorType="horary"
        testId={`hero-horary-cta${suffix}`}
        source={source}
      linkState={linkState}
      />
      <ProgramHeroCard
        to="/chakry-po-natalnoy-karte"
        title="АстроЧакры"
        subtitle="Узнай свою Точку сборки и Зону роста"
        Icon={ChakraLotusIcon}
        gradient="from-violet-500 to-violet-600"
        program="chakry"
        testId={`hero-chakry-cta${suffix}`}
        source={source}
      linkState={linkState}
      />
      <ProgramHeroCard
        to="/love-periods"
        title="Любовь и замужество"
        subtitle="Колесо года для любви"
        Icon={Heart}
        gradient="from-rose-500 to-rose-600"
        program="love"
        testId={`hero-love-cta${suffix}`}
        source={source}
      linkState={linkState}
      />
      {/* «Совместимость» (/para) — только на ру (astrofeel.ru, 02.11.2026):
          на главной ру не было ссылки на программу пары. */}
      {!SITE_NAME_IS_DEFAULT && (
        <ProgramHeroCard
          to="/para"
          title="Совместимость"
          subtitle="Твоя пара по дате рождения — рассчитать"
          Icon={HeartHandshake}
          gradient="from-pink-500 to-fuchsia-600"
          program="para"
          testId={`hero-para-cta${suffix}`}
          source={source}
          linkState={linkState}
        />
      )}
      <ProgramHeroCard
        to="/money-periods"
        title="Когда придут деньги"
        subtitle="Колесо года для денег"
        Icon={Wallet}
        gradient="from-emerald-500 to-emerald-600"
        program="money"
        testId={`hero-money-cta${suffix}`}
        source={source}
      linkState={linkState}
      />
    </>
  );
}

// Синяя плашка-заголовок раздела в PDF-копии.
function PdfSectionHeader({ children }) {
  return (
    <div
      data-pdf-block
      data-pdf-keep-with-next
      style={{
        background: PALETTE['accent-cta'][500], borderRadius: '8px',
        padding: '12px 18px', marginBottom: '14px',
      }}
    >
      <h2 style={{ fontSize: '26px', fontWeight: 800, color: '#ffffff', margin: 0 }}>{children}</h2>
    </div>
  );
}

export default function NewLandingPage() {
  const { isAuthenticated, user } = useAuth(); // gate register CTAs to guests only
  const natal = useNatalCalculator();
  const [natalUnlocked, setNatalUnlocked] = useState(false);
  const [openPanel, setOpenPanel] = useState(null);
  // Раскрыт ли разбор под полосой целиком (см. потолок в полтора экрана).
  // Сбрасывается на каждом новом разделе.
  const [panelExpanded, setPanelExpanded] = useState(false);
  // Карточка «Твой день сегодня» — только по клику плашки дня: расчёт
  // дня уходит в момент её появления, не раньше.
  const [dayOpen, setDayOpen] = useState(false);
  // Счётчик нажатий плашки дня: карточка сама встаёт под шапку на каждое
  // нажатие (и первое, и повторное), см. scrollKey в DayTodayCard.
  const [dayScrollKey, setDayScrollKey] = useState(0);
  // Упёрся ли разбор в потолок. Кнопка и затухание — только тогда: короткий
  // раздел с «Показать всё» под ним читался бы как обрыв.
  const [panelOverflows, setPanelOverflows] = useState(false);
  const panelBodyRef = useRef(null);
  // Стена регистрации натала (см. NatalLimitModal). Колесо карты бесплатно
  // для всех, за регистрацией — только расшифровка и PDF.
  //
  // Входов два, и окно должно знать, с какого пришли: текст сверху и параметр
  // place у цели natal_limit у них разные. wallBlocks — число из кнопки
  // «Открыть весь разбор», его же печатает заголовок окна.
  const [wallOpen, setWallOpen] = useState(false);
  const [wallPlace, setWallPlace] = useState('pdf');
  const [wallBlocks, setWallBlocks] = useState(0);
  // Повод запустить PDF без второго нажатия: человек пришёл в окно с кнопки
  // «Скачать PDF карты», зарегистрировался одной строкой — файл обязан
  // поехать сам. Кнопка PDF появляется в разметке только у вошедшего, то
  // есть на перерисовку позже, и сама ждёт готовности источника.
  const [pdfAutoStart, setPdfAutoStart] = useState(0);
  // Число блоков приезжает с сервера внутрь полосы превью — оно же нужно
  // мобильному дублю кнопки, который стоит выше, в гриде героя.
  const [previewTotal, setPreviewTotal] = useState(0);
  // Библиотека натальных аспектов (раздел «Сценарии жизни» и карточка полосы).
  // Приходит с сервера УЖЕ урезанной по тарифу: слой домов платный, и держать
  // его в браузере у гостя нельзя — ровно этим грешил прежний aspectCards.js.
  const [aspectLib, setAspectLib] = useState(null);
  // Ответ /natal/insights, который уже получили плашки на странице, — по нему
  // же рисуется их копия в PDF (без второго запроса).
  const [insightsData, setInsightsData] = useState(null);
  const chart = natal.chartData;
  const { loadChart } = natal;
  // Подсветка на круге из новых блоков («Показать на круге»). Новая карта —
  // подсветка снимается.
  const [wheelHighlight, setWheelHighlight] = useState(null);
  useEffect(() => { setWheelHighlight(null); }, [chart]);
  // Карта, открытая разово за 199 ₽ (calc_unlock: отпечаток — долготы
  // планет), читается как ПРО: полные дома, планеты и аспекты этой карты.
  const rawUserTier = natal.getUserTier();
  const userTier = natalUnlocked ? 'PRO' : rawUserTier;
  const isDemo = natal.isDemo;
  // Пока на колесе демо-карта, а человек заполняет форму, колесо рисует то,
  // что уже посчитано по его полям (Солнце → Луна → Асцендент).
  const wheelChart = (isDemo && natal.previewChart) ? natal.previewChart : chart;
  // Главная по умолчанию (и /?start=natal — её синоним): форма карты с
  // кругом первым экраном, блока вопросов на ней нет вовсе.
  // /?start=ask — посадочная под рекламу: вопросы первым экраном.
  const [searchParams] = useSearchParams();
  const natalFirst = searchParams.get('start') !== 'ask';
  // Время рождения неизвестно — карта пришла с сервера уже без домов и
  // углов (strip_unknown_time). Здесь остаётся убрать то, что ведёт к
  // пустым разделам: вход «Твои сферы жизни» и раздел «Дома» в PDF.
  const timeUnknown = !!(chart && chart.time_unknown);
  const panelCapped = isAuthenticated && !panelExpanded;
  const panelCut = panelCapped && panelOverflows;
  const articleLinks = useArticleLinks(chart); // 3.5: якоря на статьи (аккордеон + колесо)
  const pdfReportRef = useRef(null);

  // "Big three" — the natural shareable one-liner for a natal chart, same
  // way Sun/Moon/Ascendant summarizes a chart everywhere else in astrology.
  // Питает карточку «поделиться» под колесом (NatalShareCard).
  const sunSign = chart?.planets?.find((p) => p.name === 'Sun')?.sign_ru;
  const moonSign = chart?.planets?.find((p) => p.name === 'Moon')?.sign_ru;
  const ascSign = chart?.angles?.find((a) => a.name === 'Asc')?.sign_ru;

  // Cache key for the wheel block's rendered PDF canvas (PdfDownloadButton's
  // blockRenderCache) — built from the chart's own computed positions
  // rather than the raw birth-data form fields, which useNatalCalculator()
  // doesn't expose to this component anyway. Same birth data always
  // produces bit-identical degrees, so this is equivalent as a fingerprint
  // without needing access to the form internals. Includes userTier since
  // the wheel's rendered detail can differ by tier.
  const wheelCacheKey = chart?.planets
    ? `wheel-${userTier}-${hashFingerprint(
        [...chart.planets, ...(chart.angles || [])]
          .map((p) => `${p.name}:${p.degree?.toFixed(4)}`)
          .sort()
          .join('|'),
      )}`
    : undefined;

  useEffect(() => { setPanelExpanded(false); }, [openPanel]);

  // Замер разбора. Вкладки лежат в ленивом куске и дорисовываются после
  // первой отрисовки, поэтому следим за высотой содержимого, а не меряем раз.
  useEffect(() => {
    const el = panelBodyRef.current;
    if (!el || !panelCapped) { setPanelOverflows(false); return undefined; }
    const check = () => setPanelOverflows(el.scrollHeight - el.clientHeight > 4);
    check();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(check);
    ro.observe(el);
    if (el.firstElementChild) ro.observe(el.firstElementChild);
    return () => ro.disconnect();
  }, [openPanel, panelCapped, chart]);

  // Приоритет 4: "/" is the landing route registered in App.js — first step
  // of the admin funnel (server.py admin_get_funnel_stats: visit_landing).
  useEffect(() => {
    trackEvent('visit_landing');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Открыта ли эта карта разово за 199 ₽ — спрашиваем сервер на каждую
  // новую карту вошедшего без ПРО.
  useEffect(() => {
    setNatalUnlocked(false);
    if (!chart || isDemo || !isAuthenticated || rawUserTier === 'PRO') return undefined;
    let cancelled = false;
    checkCalcUnlock('natal', { planets: chart.planets || [], birth_date: (chart.birthData && chart.birthData.date) || null })
      .then((ok) => { if (!cancelled && ok) setNatalUnlocked(true); });
    return () => { cancelled = true; };
  }, [chart, isDemo, isAuthenticated, rawUserTier]);

  // Возврат после оплаты 199 ₽ (?unlock=…): открыть ИМЕННО купленную карту.
  // Сама главная восстанавливает свою — из профиля или формы, а купить могли
  // карту мужчины или подруги. Данные рождения лежат в сохранённом расчёте
  // рядом с планетами (на отпечаток не влияют). Ждём, пока главная поставит
  // свою карту, — тогда наш расчёт придёт последним и останется на экране.
  const unlockRestoredRef = useRef(false);
  useEffect(() => {
    if (unlockRestoredRef.current || !isAuthenticated || !chart) return undefined;
    const id = unlockIdFromUrl();
    if (!id) return undefined;
    unlockRestoredRef.current = true;
    let cancelled = false;
    loadCalcUnlock(id).then((doc) => {
      if (cancelled || !doc || doc.app !== 'natal' || !doc.payload || !doc.payload.birth) return;
      loadChart(doc.payload.birth);
    });
    return () => { cancelled = true; };
  }, [isAuthenticated, chart, loadChart]);

  // Тело покупки натала за 199 ₽: планеты — отпечаток, данные рождения — чтобы
  // после оплаты открыть именно эту карту (см. эффект выше).
  const natalUnlockPayload = () => ({
    planets: (chart && chart.planets) || [],
    birth: natal.birthParams || null,
    // Дата карты — покупка открывает и пересчёт с уточнённым временем/городом.
    birth_date: (chart && chart.birthData && chart.birthData.date) || null,
  });

  // «Показать на круге»: подсветка и плавная прокрутка к кругу.
  //
  // scrollIntoView, а не window.scrollTo: он двигает тот контейнер, который
  // на самом деле прокручивается (окно, body или обёртка), и сам учитывает
  // отступ под шапку — у #natal-wheel задан scroll-margin-top. Прокрутка
  // уходит в следующем кадре, после перерисовки подсветки; если через
  // секунду верх круга всё ещё не на экране (плавную прервали досчётом
  // блоков, жестом, ленивым куском колеса), довозим без анимации.
  const showOnWheel = (h) => {
    if (!h || typeof window === 'undefined') return;
    setWheelHighlight({ planets: h.planets || [], aspect: h.aspect || null });
    window.setTimeout(() => {
      const wheel = document.getElementById('natal-wheel');
      if (!wheel) return;
      wheel.scrollIntoView({ behavior: 'smooth', block: 'start' });
      window.setTimeout(() => {
        const top = wheel.getBoundingClientRect().top;
        if (top < headerOffset() - 40 || top > window.innerHeight * 0.5) {
          const html = document.documentElement;
          const prev = html.style.scrollBehavior;
          html.style.scrollBehavior = 'auto';
          wheel.scrollIntoView({ behavior: 'auto', block: 'start' });
          html.style.scrollBehavior = prev;
        }
      }, 1000);
    }, 0);
  };

  // Аспекты с трактовками — один запрос на карту. Токен шлём, если он есть:
  // по нему сервер и определяет, что человеку положено. Демо-карту не
  // спрашиваем — она не про этого человека.
  useEffect(() => {
    if (!chart || isDemo) { setAspectLib(null); return undefined; }
    let cancelled = false;
    const token = (() => {
      try { return localStorage.getItem('auth_token'); } catch (_e) { return null; }
    })();
    fetch(`${API_URL}/natal/aspects`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({
        planets: chart.planets || [],
        fictivePoints: chart.fictivePoints || [],
        aspects: chart.aspects || [],
        birth_date: (chart.birthData && chart.birthData.date) || null,
        // Время рождения неизвестно — дома недостоверны. Сервер сверится с
        // профилем сам; отсюда мы можем только ПОНИЗИТЬ доверие, не поднять.
        // Смотрим и на САМУ карту: гость профиля не имеет вовсе, а галочку
        // «не знаю время» ставит в форме — по одному профилю его случай
        // был бы неотличим от известного времени.
        houses_known: !(chart && chart.time_unknown) && !(user && user.birth_time_unknown),
        limit: 10,
      }),
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (!cancelled) setAspectLib((d && d.aspects) || null); })
      .catch(() => {
        // Раздел — не условие работы страницы: при сбое остаётся прежний
        // текст из корпуса фронта, а не пустой экран.
        if (!cancelled) setAspectLib(null);
      });
    return () => { cancelled = true; };
  }, [chart, isDemo, user, natalUnlocked]);

  // Единственная точка входа в расшифровку — и кнопки HERO_PANELS, и тапы по
  // колесу идут через неё.
  //
  // Стена отсюда снята: три раздела открыты всем, включая гостя. Пустой
  // экран за стеной ничего не обещал — человек не видел, что именно он
  // получит, и уходил. Теперь он читает бесплатный слой (Солнце, Луна,
  // Асцендент, роли планет, затравки аспектов) и упирается в обрыв текста
  // с кнопкой регистрации на том самом месте, где текст закончился.
  //
  // Стена осталась ровно на одном входе — «Скачать PDF карты» (setWallOpen
  // ниже, у кнопки PDF). Там просят почту как адрес доставки готового
  // расчёта, и это единственный шаг, где почта действительно нужна.
  const openInterpretation = (key) => {
    setOpenPanel((prev) => (prev === key ? null : key));
  };

  // Прокрутка к разделу, который только что открыли. Узел панели появляется
  // ПОСЛЕ перерисовки, поэтому ждём его появления, а не отмеряем таймаут на
  // глазок. setTimeout, а не requestAnimationFrame: в фоновой вкладке rAF не
  // тикает вовсе, и прокрутка не случилась бы никогда.
  const scrollToPanel = () => {
    if (typeof window === 'undefined') return;
    let tries = 0;
    const tick = () => {
      const el = document.getElementById('interpretations-panel');
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        return;
      }
      if (tries++ > 40) return;   // ~4 с: раздел лежит в ленивом куске
      window.setTimeout(tick, 100);
    };
    tick();
  };

  // Клик по голубой строке в полосе расшифровок. Раньше он только переключал
  // openPanel — раздел открывался ниже, но кадр к нему не подводился, и
  // человек видел лишь, как подсветился свёрнутый аккордеон в правой колонке.
  // Читалось это как «ничего не произошло». Теперь раздел раскрывается там же,
  // где человек смотрит: сразу под полосой, с подводкой кадра.
  //
  // Не тумблер: строка обещает «показать раздел», а не «спрятать». Второй
  // клик по той же строке должен оставить раздел открытым и просто вернуть
  // к нему кадр.
  const openSectionFromStrip = (key) => {
    setOpenPanel(key);
    scrollToPanel();
  };

  // Плашка «Твой день сегодня» в колонке программ: цель и карточка под
  // расшифровками. Подводку кадра делает сама карточка — после своей
  // отрисовки и ещё раз, когда день досчитался (см. DayTodayCard).
  const openDayCard = () => {
    ymGoal('day_card_open');
    setDayOpen(true);
    setDayScrollKey((k) => k + 1);
  };

  // Единственная точка открытия стены регистрации: place решает, какой текст
  // покажет окно и с каким параметром уйдёт natal_limit.
  const openWall = (place, blocks = 0) => {
    // Окно уже открыто — второй вызов игнорируем: он приходит от двойного
    // нажатия, и место показа подменялось бы задним числом.
    if (wallOpen) return;
    setWallPlace(place);
    setWallBlocks(blocks);
    setWallOpen(true);
  };

  // Регистрация прошла ПРЯМО В ОКНЕ. Никуда не уводим: человек вошёл, и
  // страница сама пересобирается под вошедшего — полоса превью сменяется
  // полными текстами, платный слой аспектов перезапрашивается с токеном
  // (эффект выше зависит от user), офскрин-источник PDF появляется в
  // разметке. Остаётся довести до конца ровно то действие, с которого
  // человек пришёл в окно.
  const handleWallRegistered = (place) => {
    // Окно НЕ закрываем: оно показывает адрес, на который ушло письмо с
    // входом, — опечатку человек заметит только там. Закроется само (или
    // кнопкой), а действие идёт уже сейчас, под ним.
    if (place === 'pdf') {
      // Тот же файл, что дала бы кнопка: она и запускает его, увидев новый
      // повод, и сама дожидается готовности источника.
      setPdfAutoStart((n) => n + 1);
      return;
    }
    // Из плашки «Твой день сегодня» человек пришёл за своим днём: плашка
    // сама перезапросится с токеном и перерисуется по его тарифу, а полоса
    // над ней уже сменилась полными текстами. Разбор не раскрываем и кадр
    // не уводим — день должен остаться перед глазами.
    if (place === 'day_card') return;
    // Из новых блоков (стихии, сила планет, показатели и таланты): блоки сами
    // перезапросятся с токеном и откроются по уровню — кадр не уводим.
    if (String(place).startsWith('insights')) return;
    // Остальные входы (кнопка «Открыть весь разбор» и строка в плашке под
    // колесом) пришли за расшифровкой — раскрываем разбор там же, где человек
    // смотрит, тем же способом, что у вошедшего: раздел плюс подводка кадра.
    setOpenPanel('planets');
    scrollToPanel();
  };

  // Окна, которое приходило САМО (долистал полосу разбора и завис — вход
  // auto_scroll), больше нет: решение владельца — на пути гостя никакой
  // рекламы, человек гуляет по бесплатному и регистрируется сам. Окно
  // остаётся ровно на двух нажатиях: кнопка «Открыть весь разбор» и
  // «Скачать PDF карты».

  // Кадр после «РАССЧИТАТЬ» — к верху круга, с отступом под шапку.
  //
  // Телефон: круг выше формы, человек нажал «РАССЧИТАТЬ» внизу — страница
  // встаёт к верху круга. ПК: круг и форма рядом, поэтому страница двигается
  // только если верх круга закрыт шапкой (человек прокрутил вниз, пока
  // заполнял форму); вниз без просьбы не уводим.
  const calcCount = natal.calcCount;
  useEffect(() => {
    if (!calcCount) return undefined;
    if (typeof window === 'undefined') return undefined;
    const desktop = window.innerWidth >= 1024;   // тот же порог, что lg:

    let cancelled = false;
    let tries = 0;
    let timer = 0;

    const tick = () => {
      if (cancelled) return;
      const wheel = document.getElementById('natal-wheel');
      // Плашка дня появляется в том же кадре, что и карта, — по ней видно,
      // что раскладка над кругом уже окончательная.
      const ready = document.querySelector('[data-testid="day-invite-btn"]');
      if (!wheel || !ready) {
        if (tries++ > 120) return;   // ~12 с и сдаёмся
        timer = window.setTimeout(tick, 100);
        return;
      }
      scrollUnderHeader(wheel, { onlyIfCovered: desktop });
    };

    timer = window.setTimeout(tick, 0);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [calcCount]);

  // SEO-аудит №2: NatalChartWheel was rendered without onSelect, so tapping
  // it (no hover on touch devices) did nothing. Opens the matching
  // interpretation panel below the wheel — same panels the HERO_PANELS
  // buttons already open.
  const handleWheelSelect = (data) => {
    if (data.type === 'house') openInterpretation('houses');
    else if (data.type === 'planet' || data.type === 'point') openInterpretation('planets');
    else if (data.type === 'aspect' || data.type === 'aspect_to_angle') openInterpretation('aspects');
    // 'zodiac' has no matching panel — its own tooltip (showZodiacTip) already
    // covers tap feedback for that element.
  };

  // Полоса прежних текстов с её кнопками — блок «Полный разбор карты» сразу
  // под кнопками оплаты, над «Твоей картой в программах» (решение владелицы,
  // шаг 5). Кнопки «Открыть полностью — 199 ₽» здесь больше нет: оплата —
  // один раз, под основными показателями и талантами.
  const fullReadingStrip = !chart || isDemo ? null : (isAuthenticated ? (
    /* Вошедший (и ЛАЙТ, и ПРО) — полоса в три столбца с ПОЛНЫМИ текстами его
       тарифа и кнопка, раскрывающая полный разбор здесь же, под блоком. */
    <>
      <NatalFullStrip
        chart={chart}
        userTier={userTier}
        onOpen={openSectionFromStrip}
        aspectLib={aspectLib}
      />
      <div className="mt-2 flex justify-center">
        <button
          type="button"
          onClick={() => openSectionFromStrip('planets')}
          className="rounded-lg bg-sky-600 px-5 py-2 text-[13px] font-semibold text-white transition-colors hover:bg-sky-700"
          data-testid="natal-show-full-reading"
        >
          Показать всю расшифровку
        </button>
      </div>
    </>
  ) : (
    /* Гость — превью с сервера; гейт прежний: «Открыть весь разбор» открывает
       окно регистрации (natal_preview, natal_preview_cta — внутри). */
    <NatalPreviewBlocks
      chart={chart}
      onOpen={openSectionFromStrip}
      onTotal={setPreviewTotal}
      onFullReport={(total) => openWall('full_report', total)}
    />
  ));

  return (
    <div
      className="min-h-screen bg-white"
      /* Отступ под закреплённую кнопку: без него она закрывала бы последние
         строки разбора и подвал. Высоту кнопка меряет сама и кладёт в
         --natal-sticky-h; полосы нет — переменная 0. */
      style={{ paddingBottom: 'var(--natal-sticky-h, 0px)' }}
    >
      <SEO page="home" canonicalPath="/" />

      <MainNavbar />

      {!natalFirst && <QuestionHero className="pt-16" />}

      {/* ══════════════════════ HERO — 3 balanced columns aligned top & bottom ══════════════════════ */}
      {/* pt-16 matches MainNavbar's fixed h-16 exactly — this section clears the
          fixed header on its own. */}
      <section
        className={`${natalFirst ? 'pt-16' : 'pt-4'} pb-4 px-3 md:px-4 bg-white`}
        data-testid="landing-hero"
      >
        <div className="max-w-[1400px] mx-auto">
          {/* min-h, not h: a fixed height clipped/overlapped the next section
              once the "Скачать PDF" button made COL 2 taller than 380px. */}
          {/* Порядок героя (решение владельца): программы | круг | ввод данных.
              ПК: программы — левые 3 колонки из 12; справа 9 колонок — H1 над
              кругом и формой (5 + 4). Телефон — тот же порядок столбиком:
              программы → заголовок → круг → форма → расшифровки. */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-3 md:gap-4 lg:min-h-[380px]">

            {/* ─── Программы + «Твой месяц» + «Твой день сегодня» ─── */}
            <div className={`lg:col-span-3 flex flex-col justify-start gap-2 ${HERO_TOP_OFFSET}`} data-testid="hero-interpretations-nav">
              {/* После расчёта своей карты программы открываются с её данными
                  (lib/homeHandoff); до расчёта и на демо-карте — как раньше. */}
              <ProgramHeroCards linkState={homeHandoffState(chart ? natal.birthParams : null, isDemo)} />
              <MonthHeroCard linkState={homeHandoffState(chart ? natal.birthParams : null, isDemo)} />
              <YearHeroCard linkState={homeHandoffState(chart ? natal.birthParams : null, isDemo)} />
              {/* После расчёта — плашка дня под «Твоим месяцем».
                  Строка «Твоя карта готова» под ней — только на ПК: на
                  телефоне колонка стоит выше круга, и «ниже» там врало бы.
                  Телефонная копия строки — под формой, над расшифровкой. */}
              {chart && !isDemo && (
                <ProgramHeroCard
                  onClick={openDayCard}
                  title="Твой день сегодня"
                  subtitle="Настроение по часам и аспекты дня"
                  Icon={Sun}
                  gradient="from-sky-300 to-sky-400"
                  tone="dark"
                  program="day"
                  testId="day-invite-btn"
                />
              )}
              {chart && !isDemo && (
                <div className="mt-1 hidden lg:block" data-testid="natal-ready-plate-desktop">
                  <ReadyPlate />
                </div>
              )}
            </div>

            <div className="lg:col-span-9 flex flex-col">
              <h1
                className="font-heading font-black text-slate-900 tracking-tight leading-tight whitespace-nowrap text-[clamp(15px,1.55vw,26px)] mb-2"
                data-testid="hero-h1"
              >
                ТВОЯ НАТАЛЬНАЯ КАРТА за 1 минуту
              </h1>
              <div className="grid grid-cols-1 lg:grid-cols-9 gap-3 md:gap-4">

                {/* ─── Круг карты (contained, centered) ─── */}
                {/* id — цель прокрутки после расчёта на телефоне (см. эффект выше).
                    scrollMarginTop повторяет высоту нависающего верха: сквозная
                    плашка (--promo-bar-h) плюс фиксированное меню h-16 = 4rem. Без
                    него колесо уезжало под шапку. */}
                <div
                  id="natal-wheel"
                  style={{ scrollMarginTop: 'calc(4rem + var(--promo-bar-h, 0px))' }}
                  /* Телефон: круг идёт после программ и перед формой. ПК: 5 из 9
                     колонок правой части, форма — остальные 4. */
                  className="lg:col-span-5 flex flex-col justify-center lg:justify-start items-center relative"
                  data-testid="hero-chart-wheel"
                >
                  {/* Was capped at max-w-[360px] — COL 1 (5/12 of the hero row)
                      has noticeably more room than that, so the circle read as
                      small inside its own block. Bigger cap fills the existing
                      column width instead of growing the block itself. */}
                  {wheelChart ? (
                    <div className="w-full max-w-[460px]">
                      <NatalChartWheel chartData={wheelChart} userTier={userTier} onSelect={handleWheelSelect} articleLinks={articleLinks} isGuest={!isAuthenticated} libraryAspects highlight={wheelHighlight} onHighlightClear={() => setWheelHighlight(null)} />
                      {!isDemo && wheelChart === chart && timeUnknown && (
                        <p className="mt-2 text-center text-xs leading-snug text-slate-500" data-testid="natal-time-caption">
                          {BIRTH_TIME_CAPTION}
                        </p>
                      )}
                    </div>
                  ) : (
                    <div className="w-full max-w-[460px]">
                      {hasSavedChart ? <NatalWheelSkeleton /> : <WheelPreview />}
                    </div>
                  )}
                  {chart && isDemo && wheelChart === chart && (
                    <span
                      className="absolute top-1 left-1 z-10 px-2 py-1 rounded-full text-[10px] font-semibold bg-slate-800/80 text-white pointer-events-none"
                      data-testid="demo-chart-badge"
                    >
                      Пример карты
                    </span>
                  )}
                  {/* Раньше подсказка об интерактивности была мелким серым текстом
                      под картой и терялась. Это фишка продукта — вынесли в
                      заметный бейдж прямо на карте. */}
                  {chart && (
                    <span
                      className="absolute top-1 right-1 z-10 flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold bg-accent-cta-500 text-white shadow-sm pointer-events-none"
                      data-testid="natal-chart-interactive-badge"
                    >
                      <MousePointerClick className="w-3.5 h-3.5" />
                      Интерактивная карта
                    </span>
                  )}

                  {/* Кнопки «Поделиться» под колесом больше нет: она стоит в
                      строке с «Скачать PDF карты» под «РАССЧИТАТЬ». */}
                </div>

                {/* ─── Ввод данных ─── */}
                <div className="lg:col-span-4 flex flex-col justify-start">
                  <div
                    className="bg-white border-2 border-accent-cta-300 rounded-xl px-3 py-1.5"
                    data-testid="cta-natal-card"
                  >
                    <div className="flex items-center justify-between mb-1">
                      <h3 className="font-semibold text-xs text-slate-800" data-testid="natal-card-title">
                        Введи данные рождения
                      </h3>
                      <span className="px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-blue-50 text-blue-600 flex-shrink-0 ml-2">
                        БЕСПЛАТНО
                      </span>
                    </div>
                    {natal.renderForm()}

                    {/* Под «РАССЧИТАТЬ» — одна строка из двух кнопок пополам:
                        PDF и «Поделиться» (АстроДиплом). Гейт PDF у гостя прежний:
                        кнопка с замком ведёт в окно регистрации. «Поделиться»
                        — всем, кто рассчитал свою карту; без Асцендента (время
                        неизвестно) сертификата нет, и PDF занимает строку целиком. */}
                    {chart && !isDemo && (
                      <div className="mt-2 flex gap-2">
                        <div className="flex-1 min-w-0" data-testid="hero-pdf-download">
                          {isAuthenticated ? (
                            <Suspense fallback={null}>
                              <PdfDownloadButton
                                targetRef={pdfReportRef}
                                calculatorName="Натальная карта"
                                filenamePrefix="natalnaya-karta"
                                label="Скачать PDF карты"
                                autoStartKey={pdfAutoStart}
                                className="w-full bg-sky-600 hover:bg-sky-700 text-white font-bold py-2 px-2 rounded-lg text-[13px] leading-tight shadow-md shadow-sky-200/50 hover:shadow-lg hover:shadow-sky-300/50"
                              />
                            </Suspense>
                          ) : (
                            /* Кнопка остаётся на месте и выглядит как рабочая — гость
                               должен видеть, что PDF существует, иначе стена ничего
                               не обещает. Клик ведёт в стену, а не в генерацию. */
                            <button
                              type="button"
                              onClick={() => openWall('pdf')}
                              className="w-full flex items-center justify-center gap-1.5 bg-sky-600 hover:bg-sky-700 text-white font-bold py-2 px-2 rounded-lg text-[13px] leading-tight shadow-md shadow-sky-200/50 hover:shadow-lg hover:shadow-sky-300/50 transition-shadow"
                              data-testid="hero-pdf-locked-btn"
                            >
                              <Lock className="w-3.5 h-3.5 shrink-0" />
                              Скачать PDF карты
                            </button>
                          )}
                        </div>
                        <Suspense fallback={null}>
                          <NatalShareCard
                            sunSign={sunSign}
                            moonSign={moonSign}
                            ascSign={ascSign}
                            className="flex-1 min-w-0"
                          />
                        </Suspense>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Телефон: «Твоя карта готова. Расшифровка ниже ↓» — сразу под
              формой, над новыми блоками карты. */}
          {chart && !isDemo && (
            <div className="mt-3 lg:hidden" data-testid="natal-ready-plate">
              <ReadyPlate />
            </div>
          )}

          {/* «Твой день сегодня» — компактная карточка под строкой «Твоя карта готова»,
              только после нажатия плашки дня. */}
          {chart && !isDemo && dayOpen && (
            <DayTodayCard
              birthParams={natal.birthParams}
              isAuthenticated={isAuthenticated}
              userKey={`${(user && user.id) || ''}|${userTier}`}
              onRegister={() => openWall('day_card')}
              scrollKey={dayScrollKey}
            />
          )}

          {/* Новые блоки натальной карты — сразу под строкой «Твоя карта
              готова». Полоса прежних текстов с её кнопками — блоком «Полный
              разбор карты» над «Твоей картой в программах» (шаг 5): у
              гостя в ней по-прежнему «Открыть весь разбор» (natal_preview,
              natal_preview_cta), у вошедшего — «Показать всю расшифровку». */}
          {chart && !isDemo && (
            <Suspense fallback={null}>
              <NatalInsights
                birthParams={natal.birthParams}
                refreshKey={`${(user && user.id) || ''}|${rawUserTier}|${natalUnlocked ? 1 : 0}`}
                onRegister={(place) => openWall(place, previewTotal)}
                onShowOnWheel={showOnWheel}
                unlockProps={{ getPayload: natalUnlockPayload, onUnlocked: () => setNatalUnlocked(true) }}
                fullReading={fullReadingStrip}
                onData={setInsightsData}
              />
            </Suspense>
          )}

        </div>
      </section>

      {/* ═══════ Full-width interpretations panel (opens BELOW Hero) ═══════ */}
      {openPanel && chart && (
        <section
          id="interpretations-panel"
          className="bg-accent-cta-50/60 border-y border-accent-cta-150 py-6 px-3 md:px-6 scroll-mt-16"
          data-testid={`accordion-content-${openPanel}`}
        >
          <div className="max-w-6xl mx-auto">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-accent-cta-800">
                {PANEL_LABELS[openPanel]}
              </h2>
              <button
                onClick={() => setOpenPanel(null)}
                className="flex items-center gap-1.5 text-sm text-accent-cta-600 hover:text-accent-cta-800 font-medium px-3 py-1.5 rounded-lg hover:bg-white transition-colors"
                data-testid="accordion-close-btn"
              >
                Свернуть
                <ChevronDown className="w-4 h-4 rotate-180" />
              </button>
            </div>
            {/* Вошедшему разбор открывается целиком, и одна вкладка тянулась на
                много экранов, унося всё, что ниже. Потолок — полтора экрана,
                дальше «Показать всё». Гостевой разбор не трогаем: он короче
                и обрывается своей кнопкой регистрации. */}
            <div
              className={`relative bg-white rounded-2xl border border-accent-cta-100 shadow-sm p-4 md:p-6 ${
                panelCapped ? 'max-h-[150vh] overflow-hidden' : ''}`}
              data-testid="interpretations-panel-body"
              ref={panelBodyRef}
            >
              <div>
              {openPanel === 'houses' && !timeUnknown && (
                <HousesTab
                  houses={chart.houses}
                  planets={chart.planets}
                  fictivePoints={chart.fictivePoints}
                  userTier={userTier}
                  articleLinks={articleLinks}
                  isGuest={!isAuthenticated}
                />
              )}
              {openPanel === 'planets' && (
                <PlanetsTab
                  planets={chart.planets}
                  fictivePoints={chart.fictivePoints}
                  userTier={userTier}
                  articleLinks={articleLinks}
                  isGuest={!isAuthenticated}
                />
              )}
              {openPanel === 'aspects' && (
                <AspectsTab
                  aspects={chart.aspects}
                  userTier={userTier}
                  isGuest={!isAuthenticated}
                  library={aspectLib}
                />
              )}
              </div>
              {panelCut && (
                <div className="pointer-events-none absolute inset-x-0 bottom-0 h-24 rounded-b-2xl bg-gradient-to-b from-transparent to-white" />
              )}
            </div>
            {panelCut && (
              <div className="mt-3 flex justify-center">
                <button
                  type="button"
                  onClick={() => setPanelExpanded(true)}
                  className="rounded-lg bg-sky-600 px-5 py-2 text-sm font-semibold text-white transition-colors hover:bg-sky-700"
                  data-testid="interpretations-panel-show-all"
                >
                  Показать всё
                </button>
              </div>
            )}
          </div>
        </section>
      )}

      {/* ═══════════════ CATEGORIES (светло-сиреневая зона, сразу под Hero) ═══════════════ */}
      <CategoriesBlock />

      {/* ═══════════════ EXPERT (белый, спокойный блок доверия) ═══════════════ */}
      {/* showConsultation — только здесь: страницы категорий рендерят тот же
          блок, но кнопку заказа просили поставить на главной. */}
      <ExpertBlock showConsultation />

      {/* ═══════════════ SCREEN 2 ═══════════════ */}
      {/* «Зачем мне АСТРОфил?» — выше цен (4.2), вместо таблицы сравнения. */}
      <WhyAstrofilBlock />
      <PricingBlock />
      <RegisterWhyBlock />
      <AccordionSectionsBlock />
      {/* Комментарии: тред главной */}
      <CommentsBlock thread="home" />
      <SiteFooter />

      {/* Приоритет 2 (спринт ПРО): PDF-отчёт по натальной карте. Внешний div
          уносит блок за пределы экрана и схлопывает его (height:0 +
          overflow:hidden) — не display:none, иначе layout не посчитается.
          Офскрин-позиционирование намеренно НЕ на элементе с ref: PdfDownloadButton
          клонирует именно ref-элемент (cloneNode), и инлайновый position/left
          клонируется вместе с ним — клон с left:-9999px внутри обёртки
          html2canvas выпадает из потока и не попадает в снимок (это и было
          причиной пустого PDF при непустом innerHTML). Сам ref-элемент несёт
          только width/background — при клонировании рендерится нормально. */}
      {chart && !isDemo && isAuthenticated && (
        <div
          style={{ position: 'absolute', left: '-9999px', top: 0, width: '794px', height: 0, overflow: 'hidden' }}
          aria-hidden="true"
        >
          <div
            ref={pdfReportRef}
            style={{ width: '794px', background: '#ffffff' }}
            data-testid="pdf-natal-report-source"
          >
            {/* data-pdf-block on the wheel and each section header: PdfDownloadButton
                packs every data-pdf-block element onto pages as an atomic unit (never
                split across a page boundary) whenever any exist. Each ExpandableCard
                (house/planet/aspect) carries the same marker — see ChartTabs.jsx.
                data-pdf-block-timeout: the wheel is a full SVG (with inlined base64
                icons) — a much heavier single block than any text card, and it was
                getting silently dropped by the shared 20s-per-block ceiling under
                real load. Give it its own longer budget instead of raising it for
                every block.
                data-pdf-cache-key: same chart → same wheel pixels every time, so a
                second PDF generation for this user in the same tab reuses the first
                render instead of paying for it again (PdfDownloadButton's
                blockRenderCache). Doesn't help a first, cold generation — only
                repeats within the session.
                width (not maxWidth): a live review found the wheel clipped in the
                PDF. html2canvas computes its own capture bounds for an <svg> child
                from the DOM at capture time — with maxWidth+auto-margins that's a
                resolved-percentage width, same class of ambiguity that bit the
                icon hrefs earlier. An explicit fixed width removes the ambiguity
                instead of trusting the layout resolves identically off-screen. */}
            <div
              data-pdf-block
              data-pdf-block-timeout="45000"
              data-pdf-cache-key={wheelCacheKey}
              style={{ marginBottom: '32px', width: '480px', marginLeft: 'auto', marginRight: 'auto' }}
            >
              <NatalChartWheel chartData={chart} userTier={userTier} />
            </div>

            {/* Плашки расшифровки — те же, что под строкой «Твоя карта готова»
                (стихии, сила планет, показатели, таланты), по тому же ответу
                сервера. Каждая строка плашек и каждый текст — свой
                data-pdf-block. */}
            {insightsData && (
              <div style={{ marginBottom: '28px' }}>
                <Suspense fallback={null}>
                  <NatalInsightsPrint data={insightsData} />
                </Suspense>
              </div>
            )}

            {/* data-pdf-keep-with-next on each header bar: PdfDownloadButton looks
                one block ahead for these specifically, and pushes the header to a
                fresh page instead of placing it if the section's first card
                wouldn't fit on the same page — a live review found headers
                orphaned alone at the bottom of a page with their own cards pushed
                to the next one.
                Разделы — в порядке и под названиями страницы (PANEL_LABELS):
                «Твои проявления», «Твои сферы жизни», «Сценарии жизни». */}
            <div style={{ marginBottom: '28px' }}>
              <PdfSectionHeader>{PANEL_LABELS.planets}</PdfSectionHeader>
              {/* expandAll: these tabs default to only the FIRST card open —
                  collapsed cards render no children at all, not just hidden
                  ones — so without this the PDF was missing almost all
                  interpretation text (only card #1 of each tab had content
                  in the DOM to snapshot). pdfMode: tints each card so it
                  reads as its own block instead of blending into a wall of
                  text — see ExpandableCard in ChartTabs.jsx. */}
              <PlanetsTab planets={chart.planets} fictivePoints={chart.fictivePoints} userTier={userTier} expandAll pdfMode />
            </div>

            {/* «Сферы жизни» (дома) — только когда время рождения известно. Карта
                без времени приходит с пустыми houses, и без этого условия в
                PDF оставался бы заголовок над пустотой. */}
            {!timeUnknown && (
              <div style={{ marginBottom: '28px' }}>
                <PdfSectionHeader>{PANEL_LABELS.houses}</PdfSectionHeader>
                <HousesTab houses={chart.houses} planets={chart.planets} fictivePoints={chart.fictivePoints} userTier={userTier} expandAll pdfMode />
              </div>
            )}

            {/* Аспекты — по той же библиотеке трактовок, что и на странице
                (метка слоя, знак аспекта, «Совет», «Где это проявится»). */}
            <div>
              <PdfSectionHeader>{PANEL_LABELS.aspects}</PdfSectionHeader>
              <AspectsTab aspects={chart.aspects} userTier={userTier} expandAll pdfMode library={aspectLib} />
            </div>

            {/* Последняя страница: кликабельные ссылки на программы сайта и на
                консультацию (адреса — на домен этой сборки, SITE_URL). */}
            <div style={{ marginTop: '28px' }}>
              <Suspense fallback={null}>
                <PdfLinksPage siteUrl={SITE_URL} />
              </Suspense>
            </div>
          </div>
        </div>
      )}

      {/* Закреплённая кнопка разбора — только гостю и только после его
          собственного расчёта. Узкий экран, невидимость настоящей кнопки и
          замер высоты — внутри компонента. */}
      {!isAuthenticated && chart && !isDemo && (
        <StickyFullReportCta
          total={previewTotal}
          suppressed={wallOpen}
          onOpen={() => openWall('sticky_mobile', previewTotal)}
        />
      )}

      <NatalLimitModal
        open={wallOpen}
        place={wallPlace}
        blocksTotal={wallBlocks}
        onClose={() => setWallOpen(false)}
        onRegistered={handleWallRegistered}
      />
    </div>
  );
}
